import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { captureException } from '../../utils/sentry';

/**
 * Error Boundary component to catch and handle React errors gracefully.
 * Provides a fallback UI when an error occurs in child components.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Send error to Sentry
    captureException(error, {
      componentStack: errorInfo?.componentStack,
      boundary: 'ErrorBoundary',
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="min-h-[400px] flex items-center justify-center p-6"
          role="alert"
          aria-live="assertive"
        >
          <div className="max-w-md w-full bg-surface-card border border-border rounded-xl p-6 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-error/10 flex items-center justify-center">
              <AlertTriangle size={28} className="text-error" aria-hidden="true" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              Something went wrong
            </h2>
            <p className="text-sm text-text-secondary mb-6">
              {this.props.message ||
                "We're sorry, an unexpected error occurred. Please try again or refresh the page."}
            </p>

            {/* Error details in development mode */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-xs text-text-tertiary cursor-pointer hover:text-text-secondary">
                  Error details
                </summary>
                <pre className="mt-2 p-3 bg-surface-base rounded-lg text-xs text-error overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-accent hover:bg-orange-hover text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                aria-label="Try again"
              >
                <RefreshCw size={16} aria-hidden="true" />
                Try again
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-4 py-2 bg-surface-hover hover:bg-surface-base text-text-primary border border-border rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                aria-label="Go to home page"
              >
                <Home size={16} aria-hidden="true" />
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
